import React, {useEffect, useState, memo} from 'react';
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography
} from '@mui/material';
import { DraggableDialog, PromptDialog } from '../components';
import clsx from 'clsx';
import {
  changeBasicAdjustments,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraTransform,
  selectActiveCamera
} from '../redux/camera-slice';
import { useSelector, useDispatch } from 'react-redux';
import { saveProject } from "../utilities";
import { trackToolUsage } from "../executable";
import { toast } from '../app';
import { useTranslation } from "react-i18next";
import {isEqual} from 'lodash';
import {
  useDialog,
  useUniqueProjectID,
  useViewer,
  useActiveTool
} from '../hooks';
import { registerEvent } from '../electron-modules';
import { Vector3 } from 'three';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    minHeightContent: {
      minHeight: "100px"
    },
    titleTop: {
      marginTop: theme.spacing(2.0)
    },
    warning: {
      color: theme.palette.warning.main
    },
    success: {
      color: theme.palette.success.main
    }
  }),
);

const AlignerShortcuts = () => {
  const {t} = useTranslation();

  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>
              {t("data-aligner-basic.adjust-roll")} (-):
            </Box> {t("data-aligner-basic.keypad")} 7
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>
              {t("data-aligner-basic.adjust-roll")} (+):
            </Box> {t("data-aligner-basic.keypad")} 9
          </Typography>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>
              {t("data-aligner-basic.adjust-pitch")} (-):
            </Box> {t("data-aligner-basic.keypad")} 4
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>
              {t("data-aligner-basic.adjust-pitch")} (+):
            </Box> {t("data-aligner-basic.keypad")} 6
          </Typography>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>
              {t("data-aligner-basic.adjust-yaw")} (-):
            </Box> {t("data-aligner-basic.keypad")} 1
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" >
            <Box fontWeight='bold' display='inline'>
              {t("data-aligner-basic.adjust-yaw")} (+):
            </Box> {t("data-aligner-basic.keypad")} 3
          </Typography>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export const ImageAlignerBasic = memo(() => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {t} = useTranslation();
  const alignerBasicDialog = useDialog();
  const uniqueProjectID = useUniqueProjectID();
  const {activeToolOpen, setActiveToolOpen} = useActiveTool();

  const [singleAlignment, setSingleAlignment] = useState([0, 0, 0]);
  const [closePrompt, setClosePrompt] = useState(false);
  const [hasTransform, setHasTransform] = useState(false);
  const currentCamera = useSelector(selectActiveCamera);
  const basicAdjustments = useSelector(selectBasicAdjustments);
  const cameraTransformPath = useSelector(selectCameraTransform);

  const handleClose = () => {
    alignerBasicDialog.handleClose();
    setClosePrompt(false);
  };

  const checkClose = () => {
    if (isModified) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  const resetAligner = () => {
    viewer?.resetBasicAlignerCamera(savedAngle);
    toast.success(t("toast.alignment-reset"));
  };

  const saveAdjustment = () => {
    const updated = {...basicAdjustments};

    const vector = new Vector3(...alignment);
    const valid = vector.length() !== 0;

    if (valid) {
      updated[currentCamera] = alignment;
    } else {
      delete updated[currentCamera];
    }

    dispatch(changeBasicAdjustments(updated));
    viewer?.resetBasicAlignerCamera(alignment);
    saveProject(false);

    trackToolUsage("aligner-basic");
    toast.success(t("toast.alignment-saved"));
  };

  const cancelAdvancedAlignment = () => {
    dispatch(changeCameraTransform(null));
    saveProject(false);
  };

  const checkDataModified = () => {
    if (!hasActiveCamera) return false;

    const epsilon = 1e-10;
    const dx = Math.abs(savedAngle[0] - singleAlignment[0]) < epsilon;
    const dy = Math.abs(savedAngle[1] - singleAlignment[1]) < epsilon;
    const dz = Math.abs(savedAngle[2] - singleAlignment[2]) < epsilon;

    return !(dx && dy && dz);
  };

  useEffect(() => {
    /** Keep track if a tool is currently being used */
    setActiveToolOpen(alignerBasicDialog.open);
  }, [alignerBasicDialog.open]);

  useEffect(() => {
    /** Close dialog when new project is loaded */
    alignerBasicDialog.handleClose();
  }, [uniqueProjectID]);

  useEffect(() => {
    registerEvent("open-image-aligner-basic", () => {
      if (activeToolOpen) return toast.warning(t('toast.one_tool_only'));
      alignerBasicDialog.handleOpen();
    });
  }, [activeToolOpen]);

  useEffect(() => {
    setHasTransform(cameraTransformPath !== null);
  }, [viewer, cameraTransformPath]);

  useEffect(() => {
    if (!viewer) return;

    const enabled = hasActiveCamera && alignerBasicDialog.open;
    viewer.setBasicAlignerState(enabled);
  }, [viewer, alignerBasicDialog.open, currentCamera]);

  useEffect(() => {
    // Not applicable if transform matrix is applied
    if (hasTransform) return;

    viewer?.resetBasicAlignerCamera(savedAngle);
  }, [viewer, currentCamera]);

  useEffect(() => {
    if (!viewer || !alignerBasicDialog.open) return;

    let interval = setInterval(() => {
      const angles = viewer.getBasicAlignerValue();

      /** NOTE: react will never return equal for arrays, so lodash is used */
      if (isEqual(singleAlignment, angles)) return;
      setSingleAlignment([...angles]);
    }, 50);

    return () => {
      clearInterval(interval);
    };
  }, [viewer, alignerBasicDialog.open, singleAlignment]);

  const hasActiveCamera = currentCamera !== null;
  const activeCameraName = hasActiveCamera ?
    viewer?.getCurrentCameraName() : null;

  const alignment = hasActiveCamera
    ? singleAlignment.map(x => parseFloat(x.toFixed(2)))
    : [0,0,0];

  const savedAngle = currentCamera in basicAdjustments
    ? basicAdjustments[currentCamera]
    : [0, 0, 0];

  const isModified = checkDataModified();

  return (
    <React.Fragment>
      <DraggableDialog
        open={alignerBasicDialog.open}
        title={t("data-aligner-basic.title")}
        onClose={checkClose}
      >
        <DialogContent className={classes.minHeightContent}>

          {(hasTransform) && (<Typography variant="caption" className={classes.warning}>
            {t("data-aligner-basic.global-transformation")}
          </Typography>)}

          {(hasTransform === false) && (<React.Fragment>
            {/* Current camera section */}
            <React.Fragment>
              <Typography gutterBottom color="textSecondary">
                {t("data-aligner-basic.current-camera")}:
              </Typography>

              <Typography variant="caption">
                {hasActiveCamera ? activeCameraName : t("data-aligner-basic.no-camera-selected")}
              </Typography>
            </React.Fragment>

            {/* Current adjustment section */}
            <React.Fragment>
              <Typography className={classes.titleTop} gutterBottom color="textSecondary">
                {t("data-aligner-basic.current-adjustment")}:
              </Typography>

              <Typography variant="caption">
                {hasActiveCamera  ? `[${alignment[0]}, ${alignment[1]}, ${alignment[2]}]` :
                  t("general.not-applicable")}
              </Typography>
            </React.Fragment>

            {/* Current adjustment section */}
            <React.Fragment>
              <Typography className={classes.titleTop} gutterBottom color="textSecondary">
                {t("data-aligner-basic.alignment-status")}:
              </Typography>

              <Typography variant="caption" className={clsx("", {
                [classes.success]: !isModified,
                [classes.warning]: isModified
              })}>
                {isModified ? t("data-aligner-basic.modified") : t("data-aligner-basic.saved")}
              </Typography>
            </React.Fragment>

            {/* Alignment shortcut keys */}
            <React.Fragment>
              <Typography className={classes.titleTop} gutterBottom color="textSecondary">
                {t("data-aligner-basic.alignment-shortcuts")}:
              </Typography>

              <AlignerShortcuts/>
            </React.Fragment>
          </React.Fragment>)}

        </DialogContent>

        <DialogActions>
          {(hasTransform) && (<Button onClick={cancelAdvancedAlignment} color="secondary">
            {t("data-aligner-adv.clear-alignment")}
          </Button>)}

          {(hasTransform === false) && (<React.Fragment>
            <Button
              color="secondary"
              disabled={!hasActiveCamera}
              onClick={resetAligner}
            >
              {t("buttons.reset-values")}
            </Button>

            <Button
              color="primary"
              disabled={!hasActiveCamera}
              onClick={saveAdjustment}
            >
              {t("buttons.save-adjustment")}
            </Button>
          </React.Fragment>)}
        </DialogActions>
      </DraggableDialog>

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          viewer?.resetBasicAlignerCamera(savedAngle);
          handleClose();
        }}
        title={t("data-aligner-basic.prompt-close-aligner-title")}
        prompt={t("data-aligner-basic.prompt-close-aligner-text")}
        button={t('buttons.close')}
      />
    </React.Fragment>
  );
});